import {Renderer, settings, Ticker} from 'pixi.js';
import {CoreGameConfig, IEventManagerWithAsync, IService, SystemComponentType} from '@gongxigames/framework/core/types';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {CoreEventsMap} from '@gongxigames/framework/core/events';
import {Application} from '@gongxigames/framework/core/services/system/Application';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';

export const TARGET_FPMS_DEFAULT = 0.06;

@injectable()
export class System implements IService {
    public static NAME: symbol = CoreDI.System;
    public type: SystemComponentType = 'service';
    public readonly ticker = Ticker.shared;
    public readonly app!: Application;

    @inject(CoreDI.UpdateManager)
    private _updateManager!: UpdateManager;

    @inject(CoreDI.IEventManagerWithAsync)
    private _eventManager!: IEventManagerWithAsync<CoreEventsMap>;

    @inject(CoreDI.GameConfig)
    private _gameConfig!: CoreGameConfig;

    private _running = true;
    private _freezed = false;

    private _tickHandler: (dt: number) => void = this.tick.bind(this);

    constructor() {
        this.app = this.createApp();
    }

    public get freezed(): boolean {
        return this._freezed;
    }

    public get running(): boolean {
        return this._running;
    }

    public init(): void {
        this.ticker.add(this._tickHandler);
        this.ticker.autoStart = false;
        this.ticker.start();

        this.app.addLayers(this._gameConfig.layers);

        document.body.appendChild(this.app.view);

        this.addEventListenerWebglContextLost(this.onWebglContextLost.bind(this));

        this._eventManager.on('General.System.PAUSE', this.pause.bind(this), this);
        this._eventManager.on('General.System.RESUME', this.resume.bind(this), this);
        this._eventManager.onAsync('General.System.PAUSE', this.pause.bind(this), this);
        this._eventManager.onAsync('General.System.RESUME', this.resume.bind(this), this);
    }

    public pause(): void {
        this._running = false;
    }

    public resume(): void {
        this._running = true;

        if (!this.ticker.started) {
            this.ticker.start();
        }
    }

    public freeze(): void {
        this._freezed = true;
        this.ticker.speed = 0;
        this._eventManager.emit('General.System.FREEZE');
    }

    public togglePause(): void {
        this.ticker.speed = this.ticker.speed === 0 ? 1 : 0;
    }

    public nextFrame(): void {
        if (this.ticker.speed === 0) {
            // allow the update
            this.ticker.speed = 1;

            this.ticker.update(this.ticker.lastTime + (1 / (settings?.TARGET_FPMS ?? TARGET_FPMS_DEFAULT)));

            // restore 0 speed
            this.ticker.speed = 0;
        }
    }

    private onWebglContextLost(): void {
        this._eventManager.emit('General.System.WEBGL_CONTEXT_LOST');
    }
    private addEventListenerWebglContextLost(listener: () => void): void {
        this.app.view.addEventListener('webglcontextlost', listener);
    }

    public set timeScale(value: number) {
        this.ticker.speed = value;
    }

    public destroy(): void {
        this.ticker.stop();
        this.ticker.remove(this._tickHandler);
        this.app.destroy();
    }

    private createApp(): Application {
        const options = {
            autoStart: false,
            sharedTicker: true,
            backgroundAlpha: 0
        };

        return new Application(options);
    }

    private tick(dt: number): void {
        strictCoreEmitter.emit('CORE.renderLoop.tick');

        if (this._running) {
            this._updateManager.update(dt / (settings?.TARGET_FPMS ?? TARGET_FPMS_DEFAULT));

            strictCoreEmitter.emit('CORE.renderLoop.beforeRender');
            this.app.render();
        }

        /**
         * TODO: Async events can be removed
         * for exable isted of `this._eventManager.emitAsync('General.System.PAUSE');`
         * use `strictCoreEmitter.once('CORE.renderLoop.tickEnd', () => strictCoreEmitter.emit('General.System.PAUSE'));`
         */
        this._eventManager.dispatchAsyncEvents();
    }

    public get renderer(): Renderer {
        return this.app.renderer;
    }

    public get view(): HTMLCanvasElement{
        return this.app.view;
    }
}
